import React, { useState, useEffect, useRef, useCallback } from 'react'
import styles from './ShopPage.module.scss'
import {
  getShopProducts, getShopCategories, getShopCart, addToCart,
  updateCartItem, removeCartItem, createOrderFromCart, getShopOrders, getShopProduct,
  getProductComments, addProductComment, deleteProductComment,
  getMyShopInfo, getMyShopProducts, createMyShopProduct, updateMyShopProduct,
  deleteMyShopProduct, addMyShopProductImage, deleteMyShopProductImage,
  getMyShopOrders, updateMyShopTelegram, updateMyShop
} from '../../../service/ShopService'
import Loader from '../../../components/Loader/Loader'

/* ─── mini icons ─── */
const CartSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3H5.5L5.6 3.5M7 13H17L21 5H6.5M7 13L5.6 3.5M7 13L5.5 15.5"/>
    <circle cx="7" cy="19" r="1" fill="currentColor" stroke="none"/>
    <circle cx="17" cy="19" r="1" fill="currentColor" stroke="none"/>
  </svg>
)
const TrashSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6m5 0V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2"/>
  </svg>
)
const CheckSvg = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const BoxSvg = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
)

/* ─── helpers ─── */
const fmt = (price) => Math.round(parseFloat(price || 0)).toLocaleString('ru-RU') + '\u00a0сом'

// iOS-safe date parser — handles "DD.MM.YYYY HH:MM" and ISO strings
const fmtDate = (raw, opts) => {
  if (!raw) return ''
  let d
  const m = String(raw).match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[\s T](\d{2}):(\d{2}))?/)
  if (m) {
    d = new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4] || '00'}:${m[5] || '00'}:00`)
  } else {
    // ISO format: replace space with T for iOS
    d = new Date(String(raw).replace(' ', 'T'))
  }
  if (isNaN(d)) return raw
  return d.toLocaleDateString('ru-RU', opts)
}

const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMWExYTFhIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM0NDQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7QotC+0LLQsNGAPC90ZXh0Pjwvc3ZnPg=='

const STATUS_INFO = {
  created:    { label: 'Создан',      color: '#667eea' },
  processing: { label: 'В обработке', color: '#F59E0B' },
  shipped:    { label: 'Отправлен',   color: '#10B981' },
  delivered:  { label: 'Доставлен',   color: '#4CAF50' },
  completed:  { label: 'Завершён',    color: '#22c55e' },
  cancelled:  { label: 'Отменён',     color: '#ef4444' },
}

const ShopPage = ({ embedded = false }) => {
  const [view, setView] = useState('catalog') // catalog | detail | cart | checkout | success | orders | my-products | my-orders
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [cart, setCart] = useState({ items: [] })
  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [lastOrder, setLastOrder] = useState(null)
  const [activeCategory, setActiveCategory] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortBy, setSortBy] = useState('-created_at')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [cartLoadingFor, setCartLoadingFor] = useState({})
  const [error, setError] = useState(null)
  const [addedItems, setAddedItems] = useState(new Set())
  const [inCartIds, setInCartIds] = useState(new Set())
  const [checkoutForm, setCheckoutForm] = useState({
    name: '', phone: '', city: 'Бишкек', address: '', apartment: '', notes: ''
  })
  const [checkoutErrors, setCheckoutErrors] = useState({})
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [activeImgIdx, setActiveImgIdx] = useState(0)
  const [lightboxImg, setLightboxImg] = useState(null)
  const [descExpanded, setDescExpanded] = useState(false)
  const searchTimerRef = useRef(null)
  const [isAuth, setIsAuth] = useState(!!localStorage.getItem('access_token'))
  
  // My Shop states
  const [myShop, setMyShop] = useState(null)
  const [myShopProducts, setMyShopProducts] = useState([])
  const [myShopLoading, setMyShopLoading] = useState(false)
  const [myShopPage, setMyShopPage] = useState(1)
  const [myShopTotalPages, setMyShopTotalPages] = useState(1)
  const [myShopSearchQuery, setMyShopSearchQuery] = useState('')
  const [myShopDebouncedSearch, setMyShopDebouncedSearch] = useState('')
  const [myShopSortBy, setMyShopSortBy] = useState('-created_at')
  const [editingProduct, setEditingProduct] = useState(null)
  const [showProductModal, setShowProductModal] = useState(false)
  const myShopSearchTimerRef = useRef(null)
  
  // My Shop Orders states
  const [myShopOrders, setMyShopOrders] = useState([])
  const [myShopOrdersLoading, setMyShopOrdersLoading] = useState(false)
  const [myShopOrdersPage, setMyShopOrdersPage] = useState(1)
  const [myShopOrdersTotalPages, setMyShopOrdersTotalPages] = useState(1)
  const [myShopOrdersStatusFilter, setMyShopOrdersStatusFilter] = useState('')
  
  // Telegram settings
  const [telegramUsername, setTelegramUsername] = useState('')
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramSaving, setTelegramSaving] = useState(false)
  const [telegramEditMode, setTelegramEditMode] = useState(false)
  
  // Shop editing
  const [showShopEditModal, setShowShopEditModal] = useState(false)
  const [shopEditForm, setShopEditForm] = useState({ name: '', description: '', is_active: true })
  const [shopEditLogo, setShopEditLogo] = useState(null)
  const [shopEditSaving, setShopEditSaving] = useState(false)
  
  // Notifications
  const [notification, setNotification] = useState(null)

  /* ── bootstrap ── */
  useEffect(() => {
    loadCategories()
    if (localStorage.getItem('access_token')) loadCart()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── react to login/logout ── */
  useEffect(() => {
    const handleAuth = () => {
      const authed = !!localStorage.getItem('access_token')
      setIsAuth(authed)
      if (authed) {
        loadCart()
        loadProducts()
      }
    }
    window.addEventListener('userLoggedIn', handleAuth)
    window.addEventListener('authSuccess', handleAuth)
    window.addEventListener('storage', handleAuth)
    return () => {
      window.removeEventListener('userLoggedIn', handleAuth)
      window.removeEventListener('authSuccess', handleAuth)
      window.removeEventListener('storage', handleAuth)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── debounce search ── */
  useEffect(() => {
    clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1)
    }, 400)
    return () => clearTimeout(searchTimerRef.current)
  }, [searchQuery])

  /* ── reload products when filters change ── */
  useEffect(() => {
    loadProducts()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, debouncedSearch, sortBy, page])

  /* ── sync inCartIds from cart ── */
  useEffect(() => {
    if (cart?.items) {
      setInCartIds(new Set(cart.items.map(i => i.product?.id).filter(Boolean)))
    }
  }, [cart])

  /* ── API loaders ── */
  const loadCategories = async () => {
    try {
      const res = await getShopCategories()
      setCategories(res.data.results || res.data || [])
    } catch (e) {}
  }

  const loadCart = async () => {
    try {
      const res = await getShopCart()
      setCart(res.data || { items: [] })
    } catch (e) {
      setCart({ items: [] })
    }
  }

  const loadOrders = async () => {
    setOrdersLoading(true)
    try {
      const res = await getShopOrders()
      setOrders(res.data.results || res.data || [])
    } catch (e) {
      setOrders([])
    } finally {
      setOrdersLoading(false)
    }
  }

  const loadProducts = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { ordering: sortBy, page, page_size: 12 }
      if (activeCategory) params.category = activeCategory
      if (debouncedSearch) params.search = debouncedSearch
      const res = await getShopProducts(params)
      const data = res.data
      const allProducts = data.results || data || []
      setProducts(allProducts.filter(p => p.stock > 0))
      const count = data.count || 0
      setTotalCount(count)
      setTotalPages(data.total_pages || Math.ceil(count / 12) || 1)
    } catch (e) {
      setError('Ошибка загрузки товаров. Попробуйте позже.')
    } finally {
      setLoading(false)
    }
  }

  /* ── my shop functions ── */
  const loadMyShop = async () => {
    try {
      const res = await getMyShopInfo()
      if (res.data.ok) {
        setMyShop(res.data.shop)
        setTelegramUsername(res.data.shop.telegram_username || '')
        setTelegramChatId(res.data.shop.telegram_chat_id || '')
        const hasTelegram = res.data.shop.telegram_username || res.data.shop.telegram_chat_id
        setTelegramEditMode(!hasTelegram)
      } else {
        setMyShop(null)
      }
    } catch (e) {
      setMyShop(null)
    }
  }
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 3000)
  }

  const loadMyShopProducts = async () => {
    setMyShopLoading(true)
    try {
      const params = { ordering: myShopSortBy, page: myShopPage, page_size: 12 }
      if (myShopDebouncedSearch) params.search = myShopDebouncedSearch
      const res = await getMyShopProducts(params)
      const data = res.data
      setMyShopProducts(data.results || data || [])
      const count = data.count || 0
      setMyShopTotalPages(data.total_pages || Math.ceil(count / 12) || 1)
    } catch (e) {
      setMyShopProducts([])
    } finally {
      setMyShopLoading(false)
    }
  }

  const handleCreateProduct = () => {
    setEditingProduct(null)
    setShowProductModal(true)
  }

  const handleEditProduct = (product) => {
    setEditingProduct(product)
    setShowProductModal(true)
  }

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Удалить товар?')) return
    try {
      await deleteMyShopProduct(productId)
      loadMyShopProducts()
      showNotification('Товар удален')
    } catch (e) {
      showNotification('Ошибка при удалении товара', 'error')
    }
  }
  
  const loadMyShopOrders = async () => {
    setMyShopOrdersLoading(true)
    try {
      const params = { page: myShopOrdersPage, page_size: 20 }
      if (myShopOrdersStatusFilter) params.status = myShopOrdersStatusFilter
      const res = await getMyShopOrders(params)
      setMyShopOrders(res.data.results || res.data || [])
      const count = res.data.count || 0
      setMyShopOrdersTotalPages(res.data.total_pages || Math.ceil(count / 20) || 1)
    } catch (e) {
      console.error('Failed to load my shop orders:', e)
    } finally {
      setMyShopOrdersLoading(false)
    }
  }
  
  const handleSaveTelegram = async () => {
    setTelegramSaving(true)
    try {
      const res = await updateMyShopTelegram({
        telegram_username: telegramUsername,
        telegram_chat_id: telegramChatId
      })
      if (res.data.ok) {
        setMyShop(res.data.shop)
        setTelegramEditMode(false)
        showNotification('Telegram настройки сохранены!')
      }
    } catch (e) {
      showNotification('Ошибка сохранения Telegram настроек', 'error')
    } finally {
      setTelegramSaving(false)
    }
  }

  const handleToggleShopActive = async () => {
    if (!myShop) return
    try {
      const formData = new FormData()
      formData.append('name', myShop.name)
      formData.append('is_active', !myShop.is_active)
      const res = await updateMyShop(formData)
      if (res.data.ok) {
        setMyShop(res.data.shop)
        showNotification(res.data.shop.is_active ? 'Магазин активирован' : 'Магазин деактивирован')
      }
    } catch (e) {
      showNotification('Ошибка изменения статуса магазина', 'error')
    }
  }

  const handleToggleProductActive = async (productId, currentStatus) => {
    try {
      const formData = new FormData()
      formData.append('is_active', !currentStatus)
      const res = await updateMyShopProduct(productId, formData)
      if (res.data.ok) {
        setMyShopProducts(prev => prev.map(p => 
          p.id === productId ? { ...p, is_active: !currentStatus } : p
        ))
        showNotification(res.data.product.is_active ? 'Товар активирован' : 'Товар скрыт')
      }
    } catch (e) {
      showNotification('Ошибка изменения статуса товара', 'error')
    }
  }
  
  const handleOpenShopEdit = () => {
    if (myShop) {
      setShopEditForm({
        name: myShop.name || '',
        description: myShop.description || '',
        is_active: myShop.is_active !== undefined ? myShop.is_active : true
      })
      setShopEditLogo(null)
      setShowShopEditModal(true)
    }
  }
  
  const handleSaveShopEdit = async () => {
    setShopEditSaving(true)
    try {
      const formData = new FormData()
      formData.append('name', shopEditForm.name)
      formData.append('description', shopEditForm.description)
      formData.append('is_active', shopEditForm.is_active)
      if (shopEditLogo) {
        formData.append('logo', shopEditLogo)
      }
      
      const res = await updateMyShop(formData)
      if (res.data.ok) {
        setMyShop(res.data.shop)
        setShowShopEditModal(false)
        showNotification('Магазин обновлен!')
        loadMyShop()
      }
    } catch (e) {
      showNotification('Ошибка обновления магазина', 'error')
    } finally {
      setShopEditSaving(false)
    }
  }

  /* ── debounce my shop search ── */
  useEffect(() => {
    clearTimeout(myShopSearchTimerRef.current)
    myShopSearchTimerRef.current = setTimeout(() => {
      setMyShopDebouncedSearch(myShopSearchQuery)
      setMyShopPage(1)
    }, 400)
    return () => clearTimeout(myShopSearchTimerRef.current)
  }, [myShopSearchQuery])

  /* ── reload my shop products when filters change ── */
  useEffect(() => {
    if (view === 'my-products' && myShop) {
      loadMyShopProducts()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myShopDebouncedSearch, myShopSortBy, myShopPage, view, myShop])

  /* ── load my shop info on auth ── */
  useEffect(() => {
    if (isAuth) {
      loadMyShop()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth])
  
  /* ── reload my shop orders when filters change ── */
  useEffect(() => {
    if (view === 'my-orders') {
      loadMyShopOrders()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myShopOrdersPage, myShopOrdersStatusFilter, view])

  /* ── cart actions ── */
  const handleAddToCart = async (product, e) => {
    e?.stopPropagation()
    if (!isAuth) return
    const pid = product.id
    setCartLoadingFor(prev => ({ ...prev, [pid]: true }))
    try {
      await addToCart(product.slug || product.id, 1)
      setAddedItems(prev => new Set([...prev, pid]))
      await loadCart()
      setTimeout(() => {
        setAddedItems(prev => { const n = new Set(prev); n.delete(pid); return n })
      }, 2500)
    } catch (e) {
      console.error('addToCart error:', e)
    } finally {
      setCartLoadingFor(prev => ({ ...prev, [pid]: false }))
    }
  }

  const handleProductClick = async (product) => {
    setSelectedProduct(product)
    setActiveImgIdx(0)
    setDescExpanded(false)
    setView('detail')
    try {
      const res = await getShopProduct(product.slug)
      setSelectedProduct(res.data)
      setActiveImgIdx(0)
    } catch (e) {}
  }

  const handleUpdateQty = async (itemId, newQty) => {
    if (newQty < 1) { handleRemoveItem(itemId); return }
    try {
      await updateCartItem(itemId, newQty)
      setCart(prev => ({
        ...prev,
        items: prev.items.map(i => i.id === itemId ? { ...i, quantity: newQty } : i)
      }))
    } catch (e) {}
  }

  const handleRemoveItem = async (itemId) => {
    try {
      await removeCartItem(itemId)
      setCart(prev => ({ ...prev, items: prev.items.filter(i => i.id !== itemId) }))
    } catch (e) {}
  }

  /* ── checkout ── */
  const validateCheckout = () => {
    const errs = {}
    if (!checkoutForm.name.trim()) errs.name = 'Обязательное поле'
    if (!checkoutForm.phone.trim()) errs.phone = 'Обязательное поле'
    else if (!/^\+?[\d\s\-(]{7,}$/.test(checkoutForm.phone)) errs.phone = 'Введите корректный номер'
    if (!checkoutForm.address.trim()) errs.address = 'Обязательное поле'
    setCheckoutErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmitOrder = async () => {
    if (!validateCheckout()) return
    setCheckoutLoading(true)
    try {
      const f = checkoutForm
      const notesText = [
        `Имя: ${f.name}`,
        `Телефон: ${f.phone}`,
        f.city && `Город: ${f.city}`,
        `Адрес: ${f.address}`,
        f.apartment && `Кв/Офис: ${f.apartment}`,
        f.notes && `Примечание: ${f.notes}`
      ].filter(Boolean).join('\n')
      const res = await createOrderFromCart({ notes: notesText })
      setLastOrder(res.data)
      setCart({ items: [] })
      setCheckoutForm({ name: '', phone: '', city: 'Бишкек', address: '', apartment: '', notes: '' })
      setView('success')
    } catch (e) {
      // "Корзина пуста" означает что заказ создан, бек очистил корзину — это успех
      const errMsg = e?.response?.data?.error || e?.message || ''
      if (errMsg === 'Корзина пуста') {
        setLastOrder(null)
        setCart({ items: [] })
        setCheckoutForm({ name: '', phone: '', city: 'Бишкек', address: '', apartment: '', notes: '' })
        setView('success')
      } else {
        setCheckoutErrors({ submit: 'Ошибка при оформлении. Попробуйте снова.' })
      }
    } finally {
      setCheckoutLoading(false)
    }
  }

  /* ── derived ── */
  const cartCount = cart?.items?.length || 0
  const cartTotal = cart?.items?.reduce(
    (sum, i) => sum + parseFloat(i.unit_price_snapshot || i.product?.price || 0) * i.quantity, 0
  ) || 0
  const isCatalogView = view === 'catalog' || view === 'detail'

  /* ════════════ RENDER ════════════ */
  return (
    <div className={styles.shopPage}>

      {/* ─── TOP NAV ─── */}
      <nav className={styles.topNav}>
        <div className={styles.navBrand}>
          <CartSvg />
          <span>NCORE Shop</span>
        </div>
        <div className={styles.navTabs}>
          <button
            className={`${styles.navTab} ${isCatalogView ? styles.navTabActive : ''}`}
            onClick={() => { setView('catalog'); setSelectedProduct(null) }}
          >
            Каталог
            {totalCount > 0 && <span className={styles.navTabCount}>{totalCount}</span>}
          </button>
          <button
            className={`${styles.navTab} ${(view === 'cart' || view === 'checkout') ? styles.navTabActive : ''}`}
            onClick={() => setView('cart')}
          >
            Корзина
            {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
          </button>
          {isAuth && (
            <button
              className={`${styles.navTab} ${view === 'my-products' ? styles.navTabActive : ''}`}
              onClick={() => { setView('my-products'); setMyShopPage(1) }}
            >
              Мои товары
            </button>
          )}
          {isAuth && (
            <button
              className={`${styles.navTab} ${view === 'my-orders' ? styles.navTabActive : ''}`}
              onClick={() => { setView('my-orders'); setMyShopOrdersPage(1) }}
            >
              Заказы на товары
            </button>
          )}
          {isAuth && (
            <button
              className={`${styles.navTab} ${view === 'orders' ? styles.navTabActive : ''}`}
              onClick={() => { setView('orders'); loadOrders() }}
            >
              Мои заказы
            </button>
          )}
        </div>
      </nav>

      {/* ─── MAIN CONTENT ─── */}
      <div className={styles.content}>

        {/* ══ CATALOG ══ */}
        {view === 'catalog' && (
          <div className={styles.catalogLayout}>
            <aside className={styles.sidebar}>
              <div className={styles.sidebarTitle}>Категории</div>
              <button
                className={`${styles.catBtn} ${!activeCategory ? styles.catBtnActive : ''}`}
                onClick={() => { setActiveCategory(null); setPage(1) }}
              >
                <span>Все товары</span>
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`${styles.catBtn} ${activeCategory === cat.id ? styles.catBtnActive : ''}`}
                  onClick={() => { setActiveCategory(cat.id); setPage(1) }}
                >
                  <span>{cat.name}</span>
                  {cat.products_count > 0 && <span className={styles.catCount}>{cat.products_count}</span>}
                </button>
              ))}
            </aside>

            <div className={styles.mainArea}>
              <div className={styles.controlsBar}>
                <div className={styles.searchBox}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={styles.searchIcon}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Поиск товаров..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className={styles.searchInput}
                  />
                  {searchQuery && (
                    <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>×</button>
                  )}
                </div>
                <select
                  value={sortBy}
                  onChange={e => { setSortBy(e.target.value); setPage(1) }}
                  className={styles.sortSelect}
                >
                  <option value="-created_at">Сначала новые</option>
                  <option value="created_at">Сначала старые</option>
                  <option value="price">Цена: ↑</option>
                  <option value="-price">Цена: ↓</option>
                  <option value="-rating_avg">По рейтингу</option>
                </select>
              </div>

              {error ? (
                <div className={styles.stateBox}>
                  <p>{error}</p>
                  <button onClick={loadProducts} className={styles.retryBtn}>Повторить</button>
                </div>
              ) : loading ? (
                <div className={styles.loaderWrap}><Loader /></div>
              ) : products.length === 0 ? (
                <div className={styles.stateBox}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <p>Товары не найдены</p>
                </div>
              ) : (
                <>
                  <div className={styles.productsGrid}>
                    {products.map(product => {
                      const isAdded = addedItems.has(product.id)
                      const isInCart = inCartIds.has(product.id)
                      const isLoadingThis = cartLoadingFor[product.id]
                      const hasDiscount = product.discount_price && parseFloat(product.discount_price) < parseFloat(product.price)
                      const discPct = hasDiscount ? Math.round((1 - product.discount_price / product.price) * 100) : 0

                      return (
                        <div key={product.id} className={styles.productCard} onClick={() => handleProductClick(product)}>
                          <div className={styles.cardImgWrap}>
                            <img
                              src={product.main_image || PLACEHOLDER}
                              alt={product.name}
                              className={styles.cardImg}
                              onError={e => { e.target.src = PLACEHOLDER }}
                            />
                            {hasDiscount && <div className={styles.discBadge}>−{discPct}%</div>}
                            {product.stock <= 0 && <div className={styles.soldOutOverlay}>Нет в наличии</div>}
                          </div>
                          <div className={styles.cardBody}>
                            <div className={styles.cardCatRow}>
                              <span className={styles.cardCat}>{product.category_name}</span>
                              {product.shop?.logo && (
                                <img
                                  src={product.shop.logo}
                                  alt={product.shop.name}
                                  className={styles.cardStoreLogo}
                                />
                              )}
                            </div>
                            <h3 className={styles.cardName}>{product.name}</h3>
                            {product.short_description && <p className={styles.cardDesc}>{product.short_description}</p>}
                            <div className={styles.cardMeta}>
                              <div className={styles.cardRating}>
                                {parseFloat(product.rating_avg || 0) === 0 ? (
                                  <span className={styles.noRatingCard}>Нет оценок</span>
                                ) : (
                                  <>
                                    <span className={styles.star}>★</span>
                                    <span>{parseFloat(product.rating_avg).toFixed(1)}</span>
                                    {product.reviews_count > 0 && <span className={styles.revCount}>({product.reviews_count})</span>}
                                  </>
                                )}
                              </div>
                              <div className={`${styles.stockPill} ${product.stock > 0 ? styles.inStock : styles.noStock}`}>
                                {product.stock > 0 ? 'В наличии' : 'Нет'}
                              </div>
                            </div>
                            <div className={styles.cardFooter}>
                              <div className={styles.priceBlock}>
                                {hasDiscount ? (
                                  <>
                                    <span className={styles.priceNew}>{fmt(product.discount_price)}</span>
                                    <span className={styles.priceOld}>{fmt(product.price)}</span>
                                  </>
                                ) : (
                                  <span className={styles.price}>{fmt(product.current_price || product.price)}</span>
                                )}
                              </div>
                              {!isAuth ? (
                                <button
                                  className={styles.addBtn}
                                  onClick={e => { e.stopPropagation(); window.dispatchEvent(new Event('openAuth')) }}
                                >🔑 Войти</button>
                              ) : (
                                <button
                                  className={`${styles.addBtn} ${isAdded ? styles.addBtnDone : ''} ${isInCart && !isAdded ? styles.addBtnInCart : ''}`}
                                  onClick={e => handleAddToCart(product, e)}
                                  disabled={product.stock <= 0 || isLoadingThis}
                                >
                                  {isLoadingThis ? '…'
                                    : isAdded ? '✓ В корзине'
                                    : isInCart ? '+ Ещё'
                                    : product.stock <= 0 ? 'Нет'
                                    : '+ В корзину'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
                      {[...Array(Math.min(totalPages, 7))].map((_, i) => (
                        <button
                          key={i + 1}
                          className={`${styles.pageBtn} ${page === i + 1 ? styles.pageBtnActive : ''}`}
                          onClick={() => setPage(i + 1)}
                        >{i + 1}</button>
                      ))}
                      <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ PRODUCT DETAIL ══ */}
        {view === 'detail' && selectedProduct && (
          <div className={styles.detailView}>
            <button className={styles.backBtn} onClick={() => setView('catalog')}>← Назад в каталог</button>
            <div className={styles.detailLayout}>
              <div className={styles.detailImgCol}>
                {(() => {
                  const allImgs = [
                    { url: selectedProduct.main_image || PLACEHOLDER, id: 'main' },
                    ...((selectedProduct.images || []).map(img => ({ url: img.image || img.url, id: img.id })))
                  ].filter((img, idx, arr) => img.url && (idx === 0 || img.url !== arr[0].url))
                  const activeImg = allImgs[activeImgIdx] || allImgs[0]
                  return (
                    <>
                      <div className={styles.detailMainImgWrap} onClick={() => setLightboxImg(activeImg?.url || PLACEHOLDER)}>
                        <img
                          src={activeImg?.url || PLACEHOLDER}
                          alt={selectedProduct.name}
                          className={styles.detailMainImg}
                          onError={e => { e.target.src = PLACEHOLDER }}
                        />
                        <div className={styles.zoomHint}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                          Увеличить
                        </div>
                        {selectedProduct.discount_price && parseFloat(selectedProduct.discount_price) < parseFloat(selectedProduct.price) && (
                          <div className={styles.discBadge}>
                            −{Math.round((1 - selectedProduct.discount_price / selectedProduct.price) * 100)}%
                          </div>
                        )}
                      </div>
                      {allImgs.length > 1 && (
                        <div className={styles.thumbsRow}>
                          {allImgs.map((img, idx) => (
                            <img
                              key={img.id}
                              src={img.url}
                              alt=""
                              className={`${styles.thumb} ${idx === activeImgIdx ? styles.thumbActive : ''}`}
                              onClick={() => setActiveImgIdx(idx)}
                              onError={e => { e.target.src = PLACEHOLDER }}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
              <div className={styles.detailInfoCol}>
                <div className={styles.detailCat}>{selectedProduct.category_name}</div>
                <h1 className={styles.detailName}>{selectedProduct.name}</h1>
                <div className={styles.detailRating}>
                  {parseFloat(selectedProduct.rating_avg || 0) === 0 ? (
                    <span className={styles.noRatings}>Нет оценок</span>
                  ) : (
                    <>
                      {[1,2,3,4,5].map(s => (
                        <span key={s} className={s <= Math.round(selectedProduct.rating_avg || 0) ? styles.starFilled : styles.starEmpty}>★</span>
                      ))}
                      <span className={styles.ratingVal}>{parseFloat(selectedProduct.rating_avg).toFixed(1)}</span>
                      <span className={styles.ratingRevs}>
                        {selectedProduct.reviews_count || 0}
                        {' '}{(() => {
                          const c = selectedProduct.reviews_count || 0
                          if (c === 1) return 'отзыв'
                          if (c >= 2 && c <= 4) return 'отзыва'
                          return 'отзывов'
                        })()}
                      </span>
                    </>
                  )}
                </div>
                <div className={styles.detailPrices}>
                  {selectedProduct.discount_price && parseFloat(selectedProduct.discount_price) < parseFloat(selectedProduct.price) ? (
                    <>
                      <span className={styles.detailPriceNew}>{fmt(selectedProduct.discount_price)}</span>
                      <span className={styles.detailPriceOld}>{fmt(selectedProduct.price)}</span>
                      <span className={styles.detailSavings}>
                        Выгода: {fmt(selectedProduct.price - selectedProduct.discount_price)}
                      </span>
                    </>
                  ) : (
                    <span className={styles.detailPrice}>{fmt(selectedProduct.current_price || selectedProduct.price)}</span>
                  )}
                </div>
                <div className={`${styles.detailStock} ${selectedProduct.stock > 0 ? styles.detailStockIn : styles.detailStockOut}`}>
                  {selectedProduct.stock > 0 ? `✓ В наличии · ${selectedProduct.stock} шт.` : '✗ Нет в наличии'}
                </div>
                {selectedProduct.description && (() => {
                  const cleanDesc = selectedProduct.description.replace(/^"+|"+$/g, '').replace(/\r\n/g, '\n').trim()
                  const lines = cleanDesc.split('\n')
                  const isLong = lines.length > 5
                  const preview = isLong ? lines.slice(0, 5).join('\n') : cleanDesc
                  return (
                    <div className={styles.detailDesc}>
                      <h4>Описание</h4>
                      <p style={{ whiteSpace: 'pre-line' }}>
                        {descExpanded || !isLong ? cleanDesc : preview}
                      </p>
                      {isLong && (
                        <button
                          className={styles.descToggleBtn}
                          onClick={() => setDescExpanded(v => !v)}
                        >
                          {descExpanded ? '▲ Свернуть' : '▼ Подробнее'}
                        </button>
                      )}
                    </div>
                  )
                })()}
                {selectedProduct.shop?.logo && (
                  <div className={styles.detailStoreBadge}>
                    <img
                      src={selectedProduct.shop.logo}
                      alt={selectedProduct.shop.name}
                      className={styles.detailStoreLogo}
                    />
                    <div className={styles.detailStoreInfo}>
                      <span className={styles.detailStoreLabel}>Продавец</span>
                      <span className={styles.detailStoreName}>{selectedProduct.shop.name}</span>
                    </div>
                  </div>
                )}
                <div className={styles.detailActions}>
                  {!isAuth ? (
                    <button
                      className={styles.detailAddBtn}
                      onClick={() => window.dispatchEvent(new Event('openAuth'))}
                    >
                      🔑 Войти для покупки
                    </button>
                  ) : (
                    <button
                      className={`${styles.detailAddBtn} ${addedItems.has(selectedProduct.id) ? styles.detailAddBtnDone : ''}`}
                      onClick={e => handleAddToCart(selectedProduct, e)}
                      disabled={selectedProduct.stock <= 0 || cartLoadingFor[selectedProduct.id]}
                    >
                      {cartLoadingFor[selectedProduct.id] ? 'Добавляется…'
                        : addedItems.has(selectedProduct.id) ? '✓ Добавлено в корзину'
                        : selectedProduct.stock <= 0 ? 'Нет в наличии'
                        : '+ В корзину'}
                    </button>
                  )}
                  {cartCount > 0 && (
                    <button className={styles.goCartBtn} onClick={() => setView('cart')}>
                      Перейти в корзину ({cartCount})
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* ─── REVIEW / FORUM SECTION ─── */}
            <ReviewSection
              productSlug={selectedProduct.slug}
              isAuth={isAuth}
            />
          </div>
        )}

        {/* ══ CART ══ */}
        {view === 'cart' && (
          <div className={styles.cartView}>
            <h2 className={styles.sectionTitle}>
              Корзина
              {cartCount > 0 && <span className={styles.sectionCount}>{cartCount} {cartCount === 1 ? 'товар' : cartCount < 5 ? 'товара' : 'товаров'}</span>}
            </h2>
            {!isAuth ? (
              <div className={styles.authNotice}>
                <p>Войдите в аккаунт, чтобы использовать корзину и оформлять заказы</p>
                <button onClick={() => window.dispatchEvent(new Event('openAuth'))} className={styles.authBtn}>Войти</button>
              </div>
            ) : cartCount === 0 ? (
              <div className={styles.stateBox}>
                <CartSvg />
                <p>Ваша корзина пуста</p>
                <button onClick={() => setView('catalog')} className={styles.shopBtn}>Перейти к покупкам</button>
              </div>
            ) : (
              <div className={styles.cartLayout}>
                <div className={styles.cartItems}>
                  {cart.items.map(item => {
                    const p = item.product || {}
                    const unitPrice = parseFloat(item.unit_price_snapshot || p.price || 0)
                    return (
                      <div key={item.id} className={styles.cartItem}>
                        <div className={styles.cartItemImg}>
                          <img src={p.main_image || PLACEHOLDER} alt={p.name} onError={e => { e.target.src = PLACEHOLDER }} />
                        </div>
                        <div className={styles.cartItemInfo}>
                          <div className={styles.cartItemName}>{p.name}</div>
                          <div className={styles.cartItemUnit}>{fmt(unitPrice)} за шт.</div>
                        </div>
                        <div className={styles.qtyControl}>
                          <button className={styles.qtyBtn} onClick={() => handleUpdateQty(item.id, item.quantity - 1)}>−</button>
                          <span className={styles.qtyNum}>{item.quantity}</span>
                          <button className={styles.qtyBtn} onClick={() => handleUpdateQty(item.id, item.quantity + 1)}>+</button>
                        </div>
                        <div className={styles.cartItemSub}>{fmt(unitPrice * item.quantity)}</div>
                        <button className={styles.removeBtn} onClick={() => handleRemoveItem(item.id)} title="Удалить">
                          <TrashSvg />
                        </button>
                      </div>
                    )
                  })}
                </div>
                <div className={styles.cartSummaryBox}>
                  <h3>Итого</h3>
                  <div className={styles.summaryRows}>
                    <div className={styles.summaryRow}>
                      <span>Товаров</span>
                      <span>{cartCount} шт.</span>
                    </div>
                    <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                      <span>Сумма</span>
                      <span>{fmt(cartTotal)}</span>
                    </div>
                  </div>
                  <p className={styles.cartNote}>
                    Оплата при получении. Менеджер свяжется с вами для подтверждения.
                  </p>
                  <button className={styles.checkoutBtn} onClick={() => setView('checkout')}>
                    Оформить заказ →
                  </button>
                  <button className={styles.continueBtn} onClick={() => setView('catalog')}>
                    Продолжить покупки
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ CHECKOUT ══ */}
        {view === 'checkout' && (
          <div className={styles.checkoutView}>
            <button className={styles.backBtn} onClick={() => setView('cart')}>← Назад в корзину</button>
            <div className={styles.checkoutLayout}>
              {/* Order summary panel */}
              <div className={styles.orderSummaryPanel}>
                <h3>Ваш заказ</h3>
                <div className={styles.orderSummaryItems}>
                  {cart.items.map(item => {
                    const p = item.product || {}
                    const price = parseFloat(item.unit_price_snapshot || p.price || 0)
                    return (
                      <div key={item.id} className={styles.summaryItem}>
                        <img src={p.main_image || PLACEHOLDER} alt={p.name} className={styles.summaryItemImg} onError={e => { e.target.src = PLACEHOLDER }} />
                        <div className={styles.summaryItemInfo}>
                          <div className={styles.summaryItemName}>{p.name}</div>
                          <div className={styles.summaryItemQty}>× {item.quantity}</div>
                        </div>
                        <div className={styles.summaryItemPrice}>{fmt(price * item.quantity)}</div>
                      </div>
                    )
                  })}
                </div>
                <div className={styles.summaryDivider} />
                <div className={styles.summaryFinal}>
                  <span>Итого</span>
                  <span className={styles.summaryFinalAmt}>{fmt(cartTotal)}</span>
                </div>
              </div>

              {/* Checkout form */}
              <div className={styles.checkoutForm}>
                <h2>Оформление заказа</h2>
                <p className={styles.formSubtitle}>
                  Заполните контактные данные. Менеджер свяжется для подтверждения.
                </p>

                <div className={styles.formGroup}>
                  <label>Ваше имя *</label>
                  <input type="text" placeholder="Бакытов Бакыт" value={checkoutForm.name}
                    onChange={e => setCheckoutForm(p => ({ ...p, name: e.target.value }))}
                    className={`${styles.formInput} ${checkoutErrors.name ? styles.formInputErr : ''}`} />
                  {checkoutErrors.name && <span className={styles.fieldErr}>{checkoutErrors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label>Номер телефона *</label>
                  <input type="tel" placeholder="+996 700 000 000" value={checkoutForm.phone}
                    onChange={e => setCheckoutForm(p => ({ ...p, phone: e.target.value }))}
                    className={`${styles.formInput} ${checkoutErrors.phone ? styles.formInputErr : ''}`} />
                  {checkoutErrors.phone && <span className={styles.fieldErr}>{checkoutErrors.phone}</span>}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Город</label>
                    <input type="text" placeholder="Бишкек" value={checkoutForm.city}
                      onChange={e => setCheckoutForm(p => ({ ...p, city: e.target.value }))}
                      className={styles.formInput} />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Кв / Офис</label>
                    <input type="text" placeholder="кв. 12" value={checkoutForm.apartment}
                      onChange={e => setCheckoutForm(p => ({ ...p, apartment: e.target.value }))}
                      className={styles.formInput} />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Адрес доставки *</label>
                  <input type="text" placeholder="ул. Манаса 1" value={checkoutForm.address}
                    onChange={e => setCheckoutForm(p => ({ ...p, address: e.target.value }))}
                    className={`${styles.formInput} ${checkoutErrors.address ? styles.formInputErr : ''}`} />
                  {checkoutErrors.address && <span className={styles.fieldErr}>{checkoutErrors.address}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label>Примечание к заказу</label>
                  <textarea placeholder="Удобное время доставки, пожелания…" value={checkoutForm.notes}
                    onChange={e => setCheckoutForm(p => ({ ...p, notes: e.target.value }))}
                    className={styles.formTextarea} rows={3} />
                </div>

                {checkoutErrors.submit && <div className={styles.submitErr}>{checkoutErrors.submit}</div>}

                <button className={styles.submitOrderBtn} onClick={handleSubmitOrder} disabled={checkoutLoading}>
                  {checkoutLoading ? 'Отправляем заявку…' : 'Подтвердить заказ'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ SUCCESS ══ */}
        {view === 'success' && (
          <div className={styles.successView}>
            <div className={styles.successIconWrap}><CheckSvg /></div>
            <h2 className={styles.successTitle}>Заказ оформлен!</h2>
            {lastOrder && <div className={styles.successOrderNum}>Заказ № {lastOrder.id}</div>}
            <p className={styles.successText}>
              Наш менеджер свяжется с вами в ближайшее время для подтверждения заказа и уточнения деталей доставки.
            </p>
            <p className={styles.successNote}>
              Заказ сохранён в вашем <strong>личном кабинете</strong> в разделе «Мои заказы».
            </p>
            <div className={styles.successActions}>
              {isAuth && (
                <button className={styles.myOrdersBtn} onClick={() => { setView('orders'); loadOrders() }}>
                  Мои заказы
                </button>
              )}
              <button className={styles.continueBtn} onClick={() => setView('catalog')}>
                Продолжить покупки
              </button>
            </div>
          </div>
        )}

        {/* ══ ORDERS ══ */}
        {view === 'orders' && (
          <div className={styles.ordersView}>
            <h2 className={styles.sectionTitle}>Мои заказы</h2>
            {!isAuth ? (
              <div className={styles.authNotice}>
                <p>Войдите в аккаунт для просмотра истории заказов</p>
                <a href="/login" className={styles.authBtn}>Войти</a>
              </div>
            ) : ordersLoading ? (
              <div className={styles.loaderWrap}><Loader /></div>
            ) : orders.length === 0 ? (
              <div className={styles.stateBox}>
                <BoxSvg />
                <p>У вас пока нет заказов</p>
                <button onClick={() => setView('catalog')} className={styles.shopBtn}>Перейти в каталог</button>
              </div>
            ) : (
              <div className={styles.ordersList}>
                {orders.map(order => {
                  const st = STATUS_INFO[order.status] || { label: order.status, color: '#999' }
                  const date = fmtDate(order.created_at, { day: '2-digit', month: 'long', year: 'numeric' })
                  return (
                    <div key={order.id} className={styles.orderCard}>
                      <div className={styles.orderCardHead}>
                        <div className={styles.orderNum}>Заказ № {order.id}</div>
                        <div className={styles.orderStatus} style={{ color: st.color, borderColor: `${st.color}44` }}>
                          {st.label}
                        </div>
                        <div className={styles.orderDate}>{date}</div>
                      </div>
                      {order.items && order.items.length > 0 && (
                        <div className={styles.orderItems}>
                          {order.items.map(item => (
                            <div key={item.id} className={styles.orderItem}>
                              <span className={styles.orderItemName}>{item.product?.name || 'Товар'}</span>
                              <span className={styles.orderItemQty}>× {item.quantity}</span>
                              <span className={styles.orderItemPrice}>{fmt(parseFloat(item.unit_price || 0) * item.quantity)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className={styles.orderCardFoot}>
                        <div className={styles.orderTotal}>Итого: {fmt(order.total_amount)}</div>
                        {order.notes && (
                          <div className={styles.orderDelivery}>
                            <strong>Детали доставки:</strong>
                            <pre className={styles.orderNotes}>{order.notes}</pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ MY PRODUCTS ══ */}
        {view === 'my-products' && (
          <div className={styles.myProductsView}>
            <div className={styles.myShopHeader}>
              {myShop && (
                <>
                  {myShop.logo && <img src={myShop.logo} alt={myShop.name} className={styles.myShopLogo} />}
                  <div className={styles.myShopInfo}>
                    <h2 className={styles.myShopName}>{myShop.name}</h2>
                    {myShop.description && <p className={styles.myShopDesc}>{myShop.description}</p>}
                  </div>
                  <div className={styles.shopHeaderActions}>
                    <div className={styles.toggleGroup}>
                      <span className={styles.toggleLabel}>Магазин</span>
                      <button 
                        className={`${styles.toggleSwitch} ${myShop.is_active ? styles.toggleActive : ''}`}
                        onClick={handleToggleShopActive}
                        title={myShop.is_active ? 'Деактивировать магазин' : 'Активировать магазин'}
                      >
                        <span className={styles.toggleSlider}></span>
                      </button>
                    </div>
                    <button className={styles.editShopBtn} onClick={handleOpenShopEdit}>
                      Настройки
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Telegram Settings */}
            {myShop && (
              <div className={styles.telegramSettings}>
                <h3 className={styles.telegramTitle}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.098.154.23.17.324.016.094.037.308.021.475z"/>
                  </svg>
                  Уведомления в Telegram
                </h3>
                
                {!myShop.telegram_username && !myShop.telegram_chat_id ? (
                  <div className={styles.telegramWarning}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <div className={styles.telegramWarningText}>
                      <strong>Для получения уведомлений о заказах укажите Telegram ID или Username</strong>
                      <p>Инструкция:</p>
                      <ol>
                        <li>Получите свой ID: напишите <code>/start</code> боту <a href="https://t.me/getidsbot" target="_blank" rel="noopener noreferrer">@getidsbot</a> — бот выдаст ваш username и ID</li>
                        <li>Отправьте любое сообщение боту <a href="https://t.me/NcoreShopBot" target="_blank" rel="noopener noreferrer">@NcoreShopBot</a></li>
                        <li>После этого уведомления о заказах будут приходить в Telegram</li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div className={styles.telegramSuccess}>
                    ✓ Telegram настроен. Уведомления о заказах будут приходить автоматически.
                  </div>
                )}
                
                <div className={styles.telegramForm}>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Telegram Username</label>
                      <input
                        type="text"
                        placeholder="@username"
                        value={telegramUsername}
                        onChange={e => setTelegramUsername(e.target.value)}
                        disabled={!telegramEditMode}
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Telegram Chat ID</label>
                      <input
                        type="text"
                        placeholder="123456789"
                        value={telegramChatId}
                        onChange={e => setTelegramChatId(e.target.value)}
                        disabled={!telegramEditMode}
                        className={styles.input}
                      />
                    </div>
                  </div>
                  {telegramEditMode ? (
                    <button 
                      className={styles.saveTelegramBtn} 
                      onClick={handleSaveTelegram}
                      disabled={telegramSaving}
                    >
                      {telegramSaving ? 'Сохранение...' : 'Сохранить Telegram настройки'}
                    </button>
                  ) : (
                    <button 
                      className={styles.saveTelegramBtn} 
                      onClick={() => setTelegramEditMode(true)}
                    >
                      Изменить
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className={styles.myProductsControls}>
              <div className={styles.searchBox}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={styles.searchIcon}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Поиск моих товаров..."
                  value={myShopSearchQuery}
                  onChange={e => setMyShopSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              <select
                value={myShopSortBy}
                onChange={e => { setMyShopSortBy(e.target.value); setMyShopPage(1) }}
                className={styles.sortSelect}
              >
                <option value="-created_at">Сначала новые</option>
                <option value="created_at">Сначала старые</option>
                <option value="name">По названию (А-Я)</option>
                <option value="-name">По названию (Я-А)</option>
                <option value="price">Цена: ↑</option>
                <option value="-price">Цена: ↓</option>
                <option value="-stock">По остатку</option>
              </select>
              <button className={styles.createBtn} onClick={handleCreateProduct}>
                + Добавить товар
              </button>
            </div>

            {myShopLoading ? (
              <div className={styles.loaderWrap}><Loader /></div>
            ) : myShopProducts.length === 0 ? (
              <div className={styles.stateBox}>
                <BoxSvg />
                <p>У вас пока нет товаров</p>
                <button onClick={handleCreateProduct} className={styles.shopBtn}>Добавить первый товар</button>
              </div>
            ) : (
              <>
                <div className={styles.myProductsGrid}>
                  {myShopProducts.map(product => {
                    const hasDiscount = product.discount_price && parseFloat(product.discount_price) < parseFloat(product.price)
                    return (
                      <div key={product.id} className={styles.myProductCard}>
                        <div className={styles.cardImgWrap}>
                          <img
                            src={product.main_image || PLACEHOLDER}
                            alt={product.name}
                            className={styles.cardImg}
                            onError={e => { e.target.src = PLACEHOLDER }}
                          />
                          {hasDiscount && (
                            <div className={styles.discBadge}>
                              −{Math.round((1 - product.discount_price / product.price) * 100)}%
                            </div>
                          )}
                          {!product.is_active && (
                            <div className={styles.inactiveOverlay}>Неактивен</div>
                          )}
                        </div>
                        <div className={styles.cardBody}>
                          <h3 className={styles.cardName}>{product.name}</h3>
                          <div className={styles.cardMeta}>
                            <span className={styles.stockInfo}>
                              Склад: {product.stock || 0} шт
                            </span>
                            <div className={styles.priceBlock}>
                              {hasDiscount ? (
                                <>
                                  <span className={styles.priceNew}>{fmt(product.discount_price)}</span>
                                  <span className={styles.priceOld}>{fmt(product.price)}</span>
                                </>
                              ) : (
                                <span className={styles.price}>{fmt(product.price)}</span>
                              )}
                            </div>
                          </div>
                          <div className={styles.productActions}>
                            <div className={styles.toggleGroup}>
                              <button 
                                className={`${styles.toggleSwitch} ${product.is_active ? styles.toggleActive : ''}`}
                                onClick={() => handleToggleProductActive(product.id, product.is_active)}
                                title={product.is_active ? 'Скрыть товар' : 'Показать товар'}
                              >
                                <span className={styles.toggleSlider}></span>
                              </button>
                            </div>
                            <button
                              className={styles.editBtn}
                              onClick={() => handleEditProduct(product)}
                            >
                              Редактировать
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {myShopTotalPages > 1 && (
                  <div className={styles.pagination}>
                    <button className={styles.pageBtn} disabled={myShopPage <= 1} onClick={() => setMyShopPage(p => p - 1)}>←</button>
                    {[...Array(Math.min(myShopTotalPages, 7))].map((_, i) => (
                      <button
                        key={i + 1}
                        className={`${styles.pageBtn} ${myShopPage === i + 1 ? styles.pageBtnActive : ''}`}
                        onClick={() => setMyShopPage(i + 1)}
                      >{i + 1}</button>
                    ))}
                    <button className={styles.pageBtn} disabled={myShopPage >= myShopTotalPages} onClick={() => setMyShopPage(p => p + 1)}>→</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ MY ORDERS (Shop Owner Orders) ══ */}
        {view === 'my-orders' && (
          <div className={styles.myOrdersView}>
            <h2 className={styles.sectionTitle}>
              Заказы на мои товары
              {myShopOrders.length > 0 && <span className={styles.sectionCount}>{myShopOrders.length}</span>}
            </h2>
            
            <div className={styles.myOrdersControls}>
              <select
                value={myShopOrdersStatusFilter}
                onChange={e => { setMyShopOrdersStatusFilter(e.target.value); setMyShopOrdersPage(1) }}
                className={styles.sortSelect}
              >
                <option value="">Все статусы</option>
                <option value="created">Создан</option>
                <option value="processing">В обработке</option>
                <option value="shipped">Отправлен</option>
                <option value="delivered">Доставлен</option>
                <option value="completed">Завершён</option>
                <option value="cancelled">Отменён</option>
              </select>
            </div>

            {myShopOrdersLoading ? (
              <div className={styles.loaderWrap}><Loader /></div>
            ) : myShopOrders.length === 0 ? (
              <div className={styles.stateBox}>
                <BoxSvg />
                <p>Пока нет заказов на ваши товары</p>
              </div>
            ) : (
              <>
                <div className={styles.ordersList}>
                  {myShopOrders.map(order => {
                    const st = STATUS_INFO[order.status] || { label: order.status, color: '#999' }
                    const date = fmtDate(order.created_at, { day: '2-digit', month: 'long', year: 'numeric' })
                    return (
                      <div key={order.id} className={styles.orderCard}>
                        <div className={styles.orderCardHead}>
                          <div className={styles.orderNum}>Заказ № {order.id}</div>
                          <div className={styles.orderStatus} style={{ color: st.color, borderColor: `${st.color}44` }}>
                            {st.label}
                          </div>
                          <div className={styles.orderDate}>{date}</div>
                        </div>
                        
                        <div className={styles.orderCustomer}>
                          <strong>Покупатель:</strong> {order.customer_name || 'Не указан'}
                          {order.customer_phone && <span> · 📞 {order.customer_phone}</span>}
                        </div>
                        
                        {order.items && order.items.length > 0 && (
                          <div className={styles.orderItems}>
                            {order.items.map(item => (
                              <div key={item.id} className={styles.orderItem}>
                                <span className={styles.orderItemName}>{item.product?.name || 'Товар'}</span>
                                <span className={styles.orderItemQty}>× {item.quantity}</span>
                                <span className={styles.orderItemPrice}>{fmt(parseFloat(item.unit_price || 0) * item.quantity)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className={styles.orderCardFoot}>
                          <div className={styles.orderTotal}>Итого: {fmt(order.total_amount)}</div>
                          {order.delivery_address && (
                            <div className={styles.orderDelivery}>
                              <strong>Адрес:</strong> {order.delivery_address}
                              {order.delivery_apartment && `, кв. ${order.delivery_apartment}`}
                            </div>
                          )}
                          {order.notes && (
                            <div className={styles.orderDelivery}>
                              <strong>Примечание:</strong>
                              <pre className={styles.orderNotes}>{order.notes}</pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                
                {myShopOrdersTotalPages > 1 && (
                  <div className={styles.pagination}>
                    <button className={styles.pageBtn} disabled={myShopOrdersPage <= 1} onClick={() => setMyShopOrdersPage(p => p - 1)}>←</button>
                    {[...Array(Math.min(myShopOrdersTotalPages, 7))].map((_, i) => (
                      <button
                        key={i + 1}
                        className={`${styles.pageBtn} ${myShopOrdersPage === i + 1 ? styles.pageBtnActive : ''}`}
                        onClick={() => setMyShopOrdersPage(i + 1)}
                      >{i + 1}</button>
                    ))}
                    <button className={styles.pageBtn} disabled={myShopOrdersPage >= myShopOrdersTotalPages} onClick={() => setMyShopOrdersPage(p => p + 1)}>→</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ PRODUCT MODAL ══ */}
        {showProductModal && (
          <ProductModal
            product={editingProduct}
            categories={categories}
            onClose={() => {
              setShowProductModal(false)
              setEditingProduct(null)
            }}
            onSave={() => {
              setShowProductModal(false)
              setEditingProduct(null)
              loadMyShopProducts()
            }}
          />
        )}

        {/* ══ SHOP EDIT MODAL ══ */}
        {showShopEditModal && (
          <div className={styles.modalOverlay} onClick={() => setShowShopEditModal(false)}>
            <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>Настройки магазина</h3>
                <button className={styles.modalClose} onClick={() => setShowShopEditModal(false)}>×</button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label>Название магазина *</label>
                  <input
                    type="text"
                    value={shopEditForm.name}
                    onChange={e => setShopEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Описание</label>
                  <textarea
                    value={shopEditForm.description}
                    onChange={e => setShopEditForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className={styles.textarea}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Логотип магазина</label>
                  <div className={styles.uploadZone} onClick={() => document.getElementById('shopLogoInput').click()}>
                    {shopEditLogo ? (
                      <div className={styles.uploadPreview}>
                        <img src={URL.createObjectURL(shopEditLogo)} alt="Preview" />
                        <button 
                          type="button" 
                          className={styles.uploadRemove}
                          onClick={(e) => {
                            e.stopPropagation()
                            setShopEditLogo(null)
                          }}
                        >×</button>
                      </div>
                    ) : myShop?.logo ? (
                      <div className={styles.uploadPreview}>
                        <img src={myShop.logo} alt="Current logo" />
                        <div className={styles.uploadOverlay}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                          </svg>
                          <span>Заменить логотип</span>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.uploadPlaceholder}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                        </svg>
                        <span>Добавить логотип</span>
                        <small>PNG, JPG, GIF до 10MB</small>
                      </div>
                    )}
                  </div>
                  <input
                    id="shopLogoInput"
                    type="file"
                    accept="image/*"
                    onChange={e => setShopEditLogo(e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.customCheckbox}>
                    <input
                      type="checkbox"
                      checked={shopEditForm.is_active}
                      onChange={e => setShopEditForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <span className={styles.checkmark}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className={styles.checkboxText}>Магазин активен</span>
                  </label>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowShopEditModal(false)}>
                  Отмена
                </button>
                <button 
                  type="button" 
                  className={styles.saveBtn} 
                  onClick={handleSaveShopEdit}
                  disabled={shopEditSaving || !shopEditForm.name.trim()}
                >
                  {shopEditSaving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── LIGHTBOX ─── */}
      {lightboxImg && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxImg(null)}>
          <button className={styles.lightboxClose} onClick={() => setLightboxImg(null)} aria-label="Закрыть">×</button>
          <img
            src={lightboxImg}
            alt=""
            className={styles.lightboxImg}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* ─── FLOATING CART ─── */}
      {isCatalogView && cartCount > 0 && (
        <button className={styles.floatingCart} onClick={() => setView('cart')}>
          <CartSvg />
          <span className={styles.floatingCount}>{cartCount}</span>
          <span className={styles.floatingTotal}>{fmt(cartTotal)}</span>
        </button>
      )}
      
      {/* ─── NOTIFICATION ─── */}
      {notification && (
        <div className={`${styles.notification} ${styles[`notification${notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}`]}`}>
          <div className={styles.notificationContent}>
            {notification.type === 'success' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
            {notification.type === 'error' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            )}
            <span>{notification.message}</span>
          </div>
          <button className={styles.notificationClose} onClick={() => setNotification(null)}>×</button>
        </div>
      )}
    </div>
  )
}

/* ══ Product Management Modal ══ */
const ProductModal = ({ product, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category?.id || '',
    short_description: product?.short_description || '',
    description: product?.description || '',
    price: product?.price || '',
    discount_price: product?.discount_price || '',
    stock: product?.stock || 0,
    is_active: product?.is_active !== undefined ? product.is_active : true,
  })
  const [mainImage, setMainImage] = useState(null)
  const [galleryImages, setGalleryImages] = useState([])
  const [existingImages, setExistingImages] = useState(product?.images || [])
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setErrors({})

    try {
      const data = new FormData()
      
      // Append all fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          data.append(key, formData[key])
        }
      })

      // Main image
      if (mainImage) {
        data.append('main_image', mainImage)
      }

      let savedProduct
      if (product) {
        // Update existing
        const res = await updateMyShopProduct(product.id, data)
        savedProduct = res.data.product
      } else {
        // Create new
        const res = await createMyShopProduct(data)
        savedProduct = res.data.product
      }

      // Upload gallery images if any
      if (galleryImages.length > 0 && savedProduct) {
        for (const img of galleryImages) {
          const imgData = new FormData()
          imgData.append('image', img)
          try {
            await addMyShopProductImage(savedProduct.id, imgData)
          } catch (e) {
            console.error('Error uploading gallery image:', e)
          }
        }
      }

      alert(product ? 'Товар обновлен!' : 'Товар создан!')
      onSave()
    } catch (e) {
      const errorData = e.response?.data
      if (errorData?.errors) {
        setErrors(errorData.errors)
      } else {
        alert(errorData?.error || 'Ошибка при сохранении товара')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteImage = async (imageId) => {
    if (!product || !window.confirm('Удалить изображение?')) return
    try {
      await deleteMyShopProductImage(product.id, imageId)
      setExistingImages(prev => prev.filter(img => img.id !== imageId))
      alert('Изображение удалено')
    } catch (e) {
      alert('Ошибка при удалении изображения')
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{product ? 'Редактировать товар' : 'Новый товар'}</h3>
          <button className={styles.modalClose} onClick={onClose}>×</button>
        </div>
        <form className={styles.modalBody} onSubmit={handleSubmit}>
          <div className={styles.productFormGrid}>
            {/* LEFT COLUMN - Form Fields */}
            <div className={styles.productFormLeft}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Название *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={styles.input}
                  />
                  {errors.name && <span className={styles.error}>{errors.name}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>Категория *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className={styles.select}
                  >
                    <option value="">— Выберите —</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category && <span className={styles.error}>{errors.category}</span>}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Краткое описание</label>
                <input
                  type="text"
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleChange}
                  maxLength={500}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Описание</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className={styles.textarea}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Цена (сом) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    className={styles.input}
                  />
                  {errors.price && <span className={styles.error}>{errors.price}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>Скидка (сом)</label>
                  <input
                    type="number"
                    name="discount_price"
                    value={formData.discount_price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className={styles.input}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Склад (шт) *</label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    required
                    min="0"
                    className={styles.input}
                  />
                  {errors.stock && <span className={styles.error}>{errors.stock}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.customCheckbox}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />
                    <span className={styles.checkmark}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className={styles.checkboxText}>Активен</span>
                  </label>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Images */}
            <div className={styles.productFormRight}>
              <div className={styles.formGroup}>
                <label>Главное изображение</label>
                <div className={styles.uploadZone} onClick={() => document.getElementById('mainImageInput').click()}>
                  {mainImage ? (
                    <div className={styles.uploadPreview}>
                      <img src={URL.createObjectURL(mainImage)} alt="Preview" />
                      <button 
                        type="button" 
                        className={styles.uploadRemove}
                        onClick={(e) => {
                          e.stopPropagation()
                          setMainImage(null)
                        }}
                      >×</button>
                    </div>
                  ) : product?.main_image ? (
                    <div className={styles.uploadPreview}>
                      <img src={product.main_image} alt="Current" />
                      <div className={styles.uploadOverlay}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                        </svg>
                        <span>Заменить изображение</span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.uploadPlaceholder}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                      </svg>
                      <span>Загрузить фото</span>
                      <small>PNG, JPG до 10MB</small>
                    </div>
                  )}
                </div>
                <input
                  id="mainImageInput"
                  type="file"
                  accept="image/*"
                  onChange={e => setMainImage(e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>

              {product && existingImages.length > 0 && (
                <div className={styles.formGroup}>
                  <label>Галерея</label>
                  <div className={styles.galleryGrid}>
                    {existingImages.map(img => (
                      <div key={img.id} className={styles.galleryItem}>
                        <img src={img.image || img.url} alt="" />
                        <button
                          type="button"
                          className={styles.galleryDeleteBtn}
                          onClick={() => handleDeleteImage(img.id)}
                        >×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Добавить фото</label>
                <div className={styles.uploadZone} onClick={() => document.getElementById('galleryInput').click()}>
                  {galleryImages.length > 0 ? (
                    <div className={styles.multiPreview}>
                      {galleryImages.map((file, idx) => (
                        <div key={idx} className={styles.multiPreviewItem}>
                          <img src={URL.createObjectURL(file)} alt={`Preview ${idx}`} />
                          <button
                            type="button"
                            className={styles.uploadRemove}
                            onClick={(e) => {
                              e.stopPropagation()
                              setGalleryImages(prev => prev.filter((_, i) => i !== idx))
                            }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.uploadPlaceholder}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span>Галерея</span>
                      <small>Несколько файлов</small>
                    </div>
                  )}
                </div>
                <input
                  id="galleryInput"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={e => setGalleryImages(Array.from(e.target.files))}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className={styles.saveBtn} disabled={submitting}>
              {submitting ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ShopPage

/* ═══════════════════════════════════════════════
   REVIEW / FORUM SECTION
   ═══════════════════════════════════════════════ */

const STAR_GOLD = '#fbbf24'

function StarPicker({ value, hover, onHover, onLeave, onPick, size = 28 }) {
  const active = hover || value
  return (
    <div className={styles.starPicker}>
      {[1,2,3,4,5].map(n => {
        const lit = n <= active
        return (
          <button
            key={n}
            type="button"
            className={styles.starPickerBtn}
            style={lit ? { color: STAR_GOLD } : undefined}
            data-lit={lit || undefined}
            onMouseEnter={() => onHover(n)}
            onMouseLeave={onLeave}
            onClick={() => onPick(n)}
            aria-label={`${n} звезда`}
          >★</button>
        )
      })}
    </div>
  )
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000
  if (diff < 60) return 'только что'
  if (diff < 3600) return `${Math.floor(diff / 60)} мин. назад`
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч. назад`
  if (diff < 604800) return `${Math.floor(diff / 86400)} дн. назад`
  return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function Avatar({ src, username, size = 36 }) {
  const initials = (username || '?').slice(0, 2).toUpperCase()
  const hue = (username || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360

  if (src) return (
    <img
      src={src}
      alt={username}
      className={styles.rvAvatar}
      style={{ width: size, height: size }}
      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
    />
  )
  return (
    <div
      className={styles.rvAvatarFallback}
      style={{
        width: size, height: size,
        background: `hsl(${hue},60%,45%)`,
      }}
    >{initials}</div>
  )
}

function CommentItem({ comment, isAuth, currentUsername, productSlug, onReload }) {
  const [showReply, setShowReply] = useState(false)
  const [replyBody, setReplyBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null) // id being confirmed for delete

  async function submitReply(e) {
    e.preventDefault()
    if (!replyBody.trim()) return
    setSubmitting(true)
    try {
      await addProductComment(productSlug, { body: replyBody, parent: comment.id })
      setReplyBody('')
      setShowReply(false)
      onReload()
    } catch {}
    setSubmitting(false)
  }

  async function confirmDelete() {
    if (!pendingDeleteId) return
    try {
      await deleteProductComment(productSlug, pendingDeleteId)
      onReload()
    } catch {}
    setPendingDeleteId(null)
  }

  return (
    <div className={styles.rvComment}>
      <div className={styles.rvCommentHead}>
        <Avatar src={comment.user_avatar} username={comment.user_username} />
        <div className={styles.rvCommentMeta}>
          <span className={styles.rvCommentUser}>{comment.user_username}</span>
          {comment.rating != null && (
            <span className={styles.rvCommentRating}>
              {[1,2,3,4,5].map(s => (
                <span key={s} style={{ color: s <= comment.rating ? STAR_GOLD : 'rgba(255,255,255,0.2)' }}>★</span>
              ))}
            </span>
          )}
          <span className={styles.rvCommentDate}>{timeAgo(comment.created_at)}</span>
        </div>
        {comment.is_mine && (
          <button className={styles.rvDeleteBtn} onClick={() => setPendingDeleteId(comment.id)} title="Удалить">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6"/><path d="M10 11v6M14 11v6"/><path d="m9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </button>
        )}
      </div>
      <p className={styles.rvCommentBody}>{comment.body}</p>

      {/* Replies */}
      {comment.replies?.length > 0 && (
        <div className={styles.rvReplies}>
          {comment.replies.map(reply => (
            <div key={reply.id} className={styles.rvReply}>
              <div className={styles.rvCommentHead}>
                <Avatar src={reply.user_avatar} username={reply.user_username} size={28} />
                <div className={styles.rvCommentMeta}>
                  <span className={styles.rvCommentUser}>{reply.user_username}</span>
                  <span className={styles.rvCommentDate}>{timeAgo(reply.created_at)}</span>
                </div>
                {reply.is_mine && (
                  <button className={styles.rvDeleteBtn} onClick={() => setPendingDeleteId(reply.id)} title="Удалить">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6"/><path d="M10 11v6M14 11v6"/><path d="m9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  </button>
                )}
              </div>
              <p className={styles.rvCommentBody}>{reply.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Reply toggle */}
      {isAuth && !showReply && (
        <button className={styles.rvReplyToggle} onClick={() => setShowReply(true)}>↩ Ответить</button>
      )}
      {isAuth && showReply && (
        <form className={styles.rvReplyForm} onSubmit={submitReply}>
          <textarea
            className={styles.rvInput}
            placeholder={`Ответить ${comment.user_username}…`}
            value={replyBody}
            onChange={e => setReplyBody(e.target.value)}
            rows={2}
            maxLength={1000}
          />
          <div className={styles.rvReplyFormBtns}>
            <button type="submit" className={styles.rvSubmitBtn} disabled={!replyBody.trim() || submitting}>
              {submitting ? '…' : 'Отправить'}
            </button>
            <button type="button" className={styles.rvCancelBtn} onClick={() => { setShowReply(false); setReplyBody('') }}>Отмена</button>
          </div>
        </form>
      )}

      {/* Delete confirmation modal */}
      {pendingDeleteId && (
        <div className={styles.rvDeleteOverlay} onClick={() => setPendingDeleteId(null)}>
          <div className={styles.rvDeleteModal} onClick={e => e.stopPropagation()}>
            <div className={styles.rvDeleteIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="m19 6-.867 14.142A2 2 0 0 1 16.138 22H7.862a2 2 0 0 1-1.995-1.858L5 6"/>
                <path d="M10 11v6M14 11v6"/>
                <path d="m9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
            </div>
            <h4 className={styles.rvDeleteTitle}>Удалить комментарий?</h4>
            <p className={styles.rvDeleteSub}>Это действие нельзя отменить</p>
            <div className={styles.rvDeleteBtns}>
              <button className={styles.rvCancelBtn} onClick={() => setPendingDeleteId(null)}>Отмена</button>
              <button className={styles.rvConfirmDeleteBtn} onClick={confirmDelete}>Удалить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewSection({ productSlug, isAuth }) {
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [body, setBody] = useState('')
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState('')

  const load = useCallback(async (pg = 1) => {
    setLoading(true)
    try {
      const res = await getProductComments(productSlug, pg)
      const data = res.data
      const results = data.results ?? data
      setComments(results)
      if (data.count && data.page_size) setTotalPages(Math.ceil(data.count / data.page_size))
      else setTotalPages(1)
    } catch {}
    setLoading(false)
  }, [productSlug])

  useEffect(() => { load(page) }, [productSlug, page, load])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!body.trim()) return
    setSubmitErr('')
    setSubmitting(true)
    try {
      await addProductComment(productSlug, {
        body,
        rating: rating > 0 ? rating : null,
      })
      setBody('')
      setRating(0)
      setPage(1)
      await load(1)
    } catch (err) {
      const msg = err?.response?.data?.body?.[0] || err?.response?.data?.detail || 'Ошибка при отправке'
      setSubmitErr(msg)
    }
    setSubmitting(false)
  }

  // Rating distribution from loaded comments (top-level with rating)
  const ratedComments = comments.filter(c => c.rating != null)
  const avgRating = ratedComments.length
    ? (ratedComments.reduce((s, c) => s + c.rating, 0) / ratedComments.length).toFixed(1)
    : null
  const dist = [5,4,3,2,1].map(s => ({
    star: s,
    count: ratedComments.filter(c => c.rating === s).length,
    pct: ratedComments.length ? Math.round(ratedComments.filter(c => c.rating === s).length / ratedComments.length * 100) : 0,
  }))

  return (
    <div className={styles.rvSection}>
      <h3 className={styles.rvTitle}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Обсуждение
        {comments.length > 0 && <span className={styles.rvTitleCount}>{comments.length}</span>}
      </h3>

      {/* Rating summary */}
      {ratedComments.length > 0 && (
        <div className={styles.rvRatingSummary}>
          <div className={styles.rvRatingBig}>
            <span className={styles.rvRatingNum}>{avgRating}</span>
            <div className={styles.rvRatingStars}>
              {[1,2,3,4,5].map(s => (
                <span key={s} style={{ color: s <= Math.round(avgRating) ? '#fbbf24' : 'rgba(255,255,255,0.18)', fontSize: 20 }}>★</span>
              ))}
            </div>
            <span className={styles.rvRatingTotal}>{ratedComments.length} {ratedComments.length === 1 ? 'оценка' : ratedComments.length < 5 ? 'оценки' : 'оценок'}</span>
          </div>
          <div className={styles.rvRatingBars}>
            {dist.map(({ star, count, pct }) => (
              <div key={star} className={styles.rvRatingBar}>
                <span className={styles.rvBarStar}>{star}★</span>
                <div className={styles.rvBarTrack}>
                  <div className={styles.rvBarFill} style={{ width: `${pct}%`, background: STAR_GOLD }} />
                </div>
                <span className={styles.rvBarCount}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Write a comment */}
      {isAuth ? (
        <form className={styles.rvForm} onSubmit={handleSubmit}>
          <div className={styles.rvFormHeader}>
            <span className={styles.rvFormLabel}>Оставить отзыв</span>
            <StarPicker
              value={rating}
              hover={hoverRating}
              onHover={setHoverRating}
              onLeave={() => setHoverRating(0)}
              onPick={n => setRating(prev => prev === n ? 0 : n)}
            />
          </div>
          <textarea
            className={styles.rvInput}
            placeholder="Напишите ваш отзыв или вопрос…"
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={3}
            maxLength={2000}
          />
          {submitErr && <div className={styles.rvErr}>{submitErr}</div>}
          <div className={styles.rvFormFooter}>
            <span className={styles.rvCharCount}>{body.length}/2000</span>
            <button type="submit" className={styles.rvSubmitBtn} disabled={!body.trim() || submitting}>
              {submitting ? '…' : 'Опубликовать'}
            </button>
          </div>
        </form>
      ) : (
        <div className={styles.rvAuthNotice}>
          <span>Войдите, чтобы оставить отзыв</span>
          <button className={styles.rvAuthBtn} onClick={() => window.dispatchEvent(new Event('openAuth'))}>
            Войти
          </button>
        </div>
      )}

      {/* Comment list */}
      <div className={styles.rvList}>
        {loading ? (
          <div className={styles.rvLoading}><Loader /></div>
        ) : comments.length === 0 ? (
          <div className={styles.rvEmpty}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p>Пока нет отзывов. Будьте первым!</p>
          </div>
        ) : (
          comments.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              isAuth={isAuth}
              productSlug={productSlug}
              onReload={() => load(page)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.rvPagination}>
          <button className={styles.rvPageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Назад</button>
          <span className={styles.rvPageInfo}>{page} / {totalPages}</span>
          <button className={styles.rvPageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Далее →</button>
        </div>
      )}
    </div>
  )
}